// EL ESTATUS MIGRATORIO, EN UN SOLO SITIO.
//
// Lissette, 2026-09-02, queja 11: "cuando descargamos la informacion del CRM
// el estatus migratorio sale cambiado".
//
// ── POR QUE PASABA ─────────────────────────────────────────────────────────
//
// La misma lista estaba escrita TRES veces, cada una por su cuenta, y se
// habian ido separando:
//
//   ficha del cliente   deskte-full.jsx / ImmigrationSelect
//   filtros de reporte  CustomReportsTab.jsx
//   descarga e informe  ReportsClientsView.jsx / immigrationLabel
//
// Al catalogo de la DESCARGA le faltaban dos valores que el servidor si guarda
// (clients.routes.ts / IMMIGRATION_STATUSES):
//
//   undocumented   se anadio a los filtros el 2026-09-01 y aqui no.
//   asylum         el valor antiguo de "asilado"; el servidor lo conserva a
//                  proposito para las fichas que ya lo tenian, y lo trata como
//                  el mismo estatus que `asylee` (MISMO_ESTATUS).
//
// Sin entrada en el mapa, la descarga caia a `humanizeValue`, que solo cambia
// los guiones bajos por espacios y pone mayusculas. Resultado: una persona
// guardada como `undocumented` salia "Undocumented" en un informe en castellano
// donde el filtro la llama "Indocumentado", y una guardada como `asylum` salia
// "Asylum" en vez de "Asilado / Refugiado" -- o sea, la MISMA situacion legal
// aparecia como dos estatus distintos segun cuando se hubiera capturado.
//
// Eso es "sale cambiado", y no daba ningun error: el informe se descargaba
// entero y con buena cara.
//
// ── LA REGLA, PARA QUE NO VUELVA ───────────────────────────────────────────
//
// Este fichero es el unico sitio donde se escribe la lista. Anadir un estatus
// = anadirlo aqui, y aparece a la vez en la ficha, en los filtros y en la
// descarga. Si alguna vez hace falta escribirlo en otro sitio, eso es la senal
// de que hay que importar de aqui, no de que haya que copiarlo.
//
// El catalogo del servidor manda: `clients.routes.ts / IMMIGRATION_STATUSES`.
// Si se anade uno alli, tiene que aparecer aqui o volvera a "salir cambiado".

/**
 * Los estatus que se OFRECEN para elegir, en el orden en que se enseñan.
 *
 * `asylum` NO esta: es el valor antiguo y nadie debe poder elegirlo hoy. Se
 * sigue LEYENDO (ver ALIAS) para las fichas que ya lo tienen.
 */
export const ESTATUS_MIGRATORIOS = [
  'us_citizen',
  'permanent_resident',
  // Lissette 2026-10-14: desde 2027 conserva el crédito del Marketplace
  // (CMS, guía del 5/10/2026, Ley 119-21 sec. 71301). Antes iba a "Otro".
  'cuban_haitian_entrant',
  'asylee',
  'daca',
  'tps',
  'work_permit',
  'visa_holder',
  'pending',
  'undocumented',
  'other',
];

/**
 * Valores antiguos y a que estatus de hoy equivalen.
 *
 * El servidor guarda los dos y los trata como el mismo (MISMO_ESTATUS en
 * customReportFilter.ts). Aqui se traducen al entrar, para que una ficha
 * antigua se vea y se descargue igual que una nueva.
 */
export const ALIAS = { asylum: 'asylee' };

/** El valor de hoy para un valor guardado, sea antiguo o no. */
export function normalizarEstatus(v) {
  const s = String(v ?? '').trim();
  if (!s) return '';
  return ALIAS[s] || s;
}

const ETIQUETAS_ES = {
  us_citizen: 'Ciudadano EE.UU.',
  permanent_resident: 'Residente Permanente (Green Card)',
  cuban_haitian_entrant: 'Entrante cubano/haitiano (parole)',
  asylee: 'Asilado / Refugiado',
  daca: 'DACA',
  tps: 'TPS',
  work_permit: 'Permiso de Trabajo (EAD)',
  visa_holder: 'Titular de Visa',
  pending: 'En Proceso',
  undocumented: 'Indocumentado',
  other: 'Otro',
};

const ETIQUETAS_EN = {
  us_citizen: 'U.S. Citizen',
  permanent_resident: 'Permanent Resident (Green Card)',
  cuban_haitian_entrant: 'Cuban/Haitian entrant (parole)',
  asylee: 'Asylee / Refugee',
  daca: 'DACA',
  tps: 'TPS',
  work_permit: 'Work Permit (EAD)',
  visa_holder: 'Visa Holder',
  pending: 'Pending / In Process',
  undocumented: 'Undocumented',
  other: 'Other',
};

/**
 * ── LOS QUE AÑADE LA AGENCIA ────────────────────────────────────────────────
 *
 * Lissette, 2026-09-16: *"poder escribir estatus migratorio y que se vayan
 * quedando guardados en el drop down"*.
 *
 * OJO CON LO QUE ESTO **NO** ES. No es una casilla de texto libre por persona.
 * Eso ya se probó con el origen del lead (#1822) y produjo gemelos —"Parole",
 * "parole", "PAROLE"— que ensucian el filtro, el reporte y la descarga, y que
 * hacen que la MISMA situación legal cuente como tres. Aquí se escribe UNA VEZ,
 * se guarda en el catálogo de la agencia con su id, y a partir de ahí se ELIGE
 * de la lista como cualquier otro. Es el mismo trato que `leadSources.js` y que
 * los tipos de póliza propios.
 *
 * Se guarda en los ajustes de la agencia (`immigrationStatuses`), así que lo que
 * escribe una persona lo ven todas y sobrevive al navegador.
 */
let _deLaAgencia = [];

/** Hidrata el catálogo propio desde los ajustes de la agencia. */
export function setAgencyEstatusMigratorios(lista) {
  const limpio = [];
  const vistos = new Set(ESTATUS_MIGRATORIOS);
  for (const x of Array.isArray(lista) ? lista : []) {
    const id = String(x?.id ?? '').trim();
    const nombre = String(x?.nombre ?? x?.label ?? '').trim();
    // Sin id o sin nombre no se puede ni guardar ni enseñar; y un id que pise
    // uno de fábrica cambiaría en silencio lo que significa "residente".
    if (!id || !nombre || vistos.has(id)) continue;
    vistos.add(id);
    limpio.push({ id, nombre, nombreEn: String(x?.nombreEn ?? x?.labelEn ?? '').trim() || nombre });
  }
  _deLaAgencia = limpio;
}

/** Los de la agencia, como copias: tocar lo devuelto no cambia el catálogo. */
export function getAgencyEstatusMigratorios() {
  return _deLaAgencia.map((x) => ({ ...x }));
}

/**
 * Todos los que se OFRECEN: los de fábrica y detrás los de la agencia.
 *
 * Se calcula al pedirla y no se congela en una constante: el catálogo propio se
 * hidrata cuando llegan los ajustes, después de que se importe este archivo.
 */
export function estatusParaElegir() {
  return [...ESTATUS_MIGRATORIOS, ..._deLaAgencia.map((x) => x.id)];
}

/**
 * El id de un estatus nuevo, a partir de lo que escriba la agencia.
 *
 * Se saca del texto y no de la fecha para que "Parole humanitario" quede como
 * `parole_humanitario` — legible en la base, en el reporte y en la descarga,
 * que es donde se guarda el id y no la etiqueta. Misma regla que
 * `idParaOrigenNuevo` en leadSources.js.
 *
 * El id cumple la forma que el servidor acepta: minúsculas, números y guion
 * bajo, hasta 30 letras.
 */
export function idParaEstatusNuevo(nombre, existentes) {
  const base = String(nombre || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 26);
  const raiz = base || 'estatus';
  const usados = new Set([...(existentes || []), ...ESTATUS_MIGRATORIOS].map((x) => String(x).toLowerCase()));
  if (!usados.has(raiz)) return raiz;
  let n = 2;
  while (usados.has(`${raiz}_${n}`)) n += 1;
  return `${raiz}_${n}`;
}

/** La forma de id que el servidor acepta. Espejo de clients.routes.ts. */
export const FORMA_DE_ID = /^[a-z0-9_]{1,30}$/;

/**
 * ── COMO SE ESCRIBE EL ESTATUS EN UNA HOJA DE CALCULO ──────────────────────
 *
 * Las formas EXACTAS con las que llega el dato de fuera: una importacion de
 * Excel, una hoja de Monday, la columna de un PDF. "ciudadano", "green card",
 * "permiso de trabajo"... Cada clave es el texto tal cual (en minusculas) y el
 * valor, el estatus con el que se guarda.
 *
 * Vivia en `api.js`. Se muda aqui porque es la MISMA lista que este archivo ya
 * gobierna, y porque quien la tenia al lado se olvido de ella el dia que el
 * catalogo dejo de ser cerrado (ver `estatusMigratorioParaGuardar`). Conserva
 * el nombre en ingles a proposito: `api.js` lo reexporta y `PoliciesView.jsx`
 * lo importa asi, y renombrarlo no arregla nada y toca dos archivos mas.
 *
 * NO confundir con `estatusDesdeTexto`, que esta mas abajo. Aquella adivina por
 * PARECIDO ("titular de visa de estudiante" -> `visa_holder`) y es la correcta
 * para una casilla escrita a mano. Esta compara la cadena ENTERA y solo acierta
 * con lo que reconoce. La diferencia importa justo donde se usa: un id que se
 * invento la agencia (`visa_de_estudiante`) tiene que pasar intacto, y el que
 * adivina por parecido lo convertiria en `visa_holder` sin decir nada.
 */
export const IMMIGRATION_MAP = {
  us_citizen: 'us_citizen', 'us citizen': 'us_citizen', citizen: 'us_citizen',
  ciudadano: 'us_citizen', ciudadana: 'us_citizen',
  permanent_resident: 'permanent_resident', 'permanent resident': 'permanent_resident',
  resident: 'permanent_resident', residente: 'permanent_resident',
  green_card: 'permanent_resident', 'green card': 'permanent_resident',
  visa_holder: 'visa_holder', 'visa holder': 'visa_holder', visa: 'visa_holder',
  asylee: 'asylee', asylum: 'asylum', asilo: 'asylum',
  daca: 'daca',
  tps: 'tps',
  work_permit: 'work_permit', 'work permit': 'work_permit',
  permiso_de_trabajo: 'work_permit', 'permiso de trabajo': 'work_permit',
  ead: 'work_permit', 'employment authorization': 'work_permit',
  pending: 'pending', pendiente: 'pending', 'en proceso': 'pending',
  undocumented: 'undocumented', indocumentado: 'undocumented', indocumentada: 'undocumented',
  other: 'other', otro: 'other', otra: 'other',
};

/**
 * EL ESTATUS MIGRATORIO TAL COMO SE MANDA AL SERVIDOR.
 *
 * ── LO QUE ROMPIA ──────────────────────────────────────────────────────────
 *
 * Wendy Cercio, 2026-09-17, mirando por que no le cuadraban los ciudadanos.
 * El camino de guardar hacia esto:
 *
 *   IMMIGRATION_MAP['parole_humanitario'] || null   ->   null
 *
 * O sea: la agencia escribia su estatus (Lissette, 2026-09-16: *"poder escribir
 * estatus migratorio y que se vayan quedando guardados en el drop down"*), el
 * catalogo lo guardaba, el desplegable lo ofrecia, el SERVIDOR lo aceptaba
 * —desde ese mismo dia acepta cualquier id de la agencia, ver `estatusMigratorio()`
 * en clients.routes.ts— y el navegador lo convertia en vacio en el ultimo paso,
 * antes de mandarlo. Se elegia, se guardaba, y la ficha volvia en blanco.
 *
 * Sin error y sin aviso: el guardado respondia 200. Y cada ficha que pasaba por
 * ahi bajaba en uno la cuenta del desplegable de Clientes, asi que los numeros
 * cambiaban solos de un dia para otro. Es la trampa de las CAPAS que ya esta
 * escrita en CLAUDE.md: la opcion del formulario existia, el enum del servidor
 * existia, y la traduccion del medio se quedo sin enterarse.
 *
 * Le pasa igual al del conyuge y al de los dependientes, que salen de este
 * mismo sitio.
 *
 * ── LA REGLA ───────────────────────────────────────────────────────────────
 *
 * La MISMA que el servidor, y en este orden:
 *
 *   1. Vacio -> null. Asi es como se borra el campo a proposito, y tiene que
 *      seguir funcionando.
 *   2. Una forma conocida (IMMIGRATION_MAP) -> el estatus de fabrica. Va
 *      PRIMERO: "ciudadano" cumple la forma de un id, y sin esta linea se
 *      guardaria como un estatus propio llamado `ciudadano` en vez de como
 *      `us_citizen` — dos casillas para la misma situacion legal, que es
 *      exactamente el gemelo que todo este archivo existe para evitar.
 *   3. Un id con la forma que acuña `idParaEstatusNuevo` -> pasa intacto.
 *   4. Cualquier otra cosa -> null, como hasta ahora.
 *
 * Se comprueba la FORMA, no que el id este en el catalogo de la agencia. El
 * catalogo se hidrata cuando llegan los ajustes, DESPUES del primer dibujado
 * (`setAgencyEstatusMigratorios`): exigirlo aqui volveria a borrar el estatus
 * cada vez que alguien guardase una ficha antes de que llegaran los ajustes —
 * el mismo "a veces si y a veces no" del #2098. Es tambien lo que decidio el
 * servidor, y por el mismo motivo.
 *
 * La forma se mira SIN pasar a minusculas: un "Parole" con mayuscula no es un
 * id nuestro —los nuestros nacen en minusculas— sino texto escrito a mano, y
 * colarlo crearia el gemelo de "parole". El servidor lo rechaza igual.
 */
export function estatusMigratorioParaGuardar(valor) {
  if (valor === null || valor === undefined) return null;
  const s = String(valor).trim();
  if (!s) return null;
  // `hasOwnProperty` y no `MAPA[k] || null`: sin el, un valor llamado
  // "constructor" devolveria la funcion del prototipo de Object y saldria por
  // el cable como estatus.
  const k = s.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(IMMIGRATION_MAP, k)) return IMMIGRATION_MAP[k];
  return FORMA_DE_ID.test(s) ? s : null;
}

/**
 * Como se ESCRIBE un estatus para una persona.
 *
 * Vacio si no hay nada guardado. Un valor que no reconozca se devuelve tal
 * cual, sin adornarlo: si algun dia el servidor guarda uno nuevo, es mejor
 * verlo crudo -- y que cante -- que disfrazarlo de etiqueta y que parezca
 * correcto. Esa es justamente la trampa que produjo esta queja.
 */
export function etiquetaDeEstatus(v, es) {
  const k = normalizarEstatus(v);
  if (!k) return '';
  return etiquetasDeEstatus(es)[k] || k;
}

/**
 * Las etiquetas completas, para pantallas que necesitan el mapa entero.
 *
 * Incluye las de la agencia: sin ellas, el estatus que alguien acaba de
 * escribir saldria como su id crudo (`parole_humanitario`) en la ficha, en el
 * filtro y en la descarga — justo en el que mas se va a mirar.
 */
export function etiquetasDeEstatus(es) {
  const base = es ? { ...ETIQUETAS_ES } : { ...ETIQUETAS_EN };
  for (const x of _deLaAgencia) base[x.id] = es ? x.nombre : (x.nombreEn || x.nombre);
  return base;
}

/**
 * El estatus a partir de TEXTO LIBRE, tal como lo escribe un agente a mano
 * en una planilla o en una columna de Excel ("residente", "green card",
 * "permiso de trabajo", "ciudadana", "TPS"...).
 *
 * Devuelve uno de ESTATUS_MIGRATORIOS, o cadena vacia cuando no reconoce
 * lo que hay escrito. Vacio NO es "otro": significa que nadie lo ha
 * decidido todavia y quien importa lo elige en la pantalla de revision.
 * Poner "other" por nuestra cuenta seria inventarle una situacion legal a
 * una persona.
 *
 * El orden de las pruebas importa, por lo mismo de siempre: "residente
 * permanente" contiene "permanente", y "no ciudadano" contiene
 * "ciudadano" — asi que la negacion se mira ANTES.
 */
export function estatusDesdeTexto(v) {
  const s = String(v ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return '';
  // Un valor que YA es una clave del catalogo entra tal cual (incluido el
  // antiguo `asylum`, que ALIAS traduce a `asylee`).
  const directo = normalizarEstatus(s.replace(/ /g, '_'));
  if (ESTATUS_MIGRATORIOS.includes(directo)) return directo;

  if (/\bno (es )?(ciudadan|residen)/.test(s)) return '';
  if (/indocument|sin papeles|ilegal|no ?status/.test(s)) return 'undocumented';
  if (/ciudadan|citizen|\busc\b|\bus c\b|naturaliz/.test(s)) return 'us_citizen';
  if (/residen|green ?card|\bgc\b|\blpr\b|permanent/.test(s)) return 'permanent_resident';
  if (/asil|asyl|refugiad|refugee/.test(s)) return 'asylee';
  if (/\bdaca\b|accion diferida|deferred action/.test(s)) return 'daca';
  if (/\btps\b|proteccion temporal|temporary protected/.test(s)) return 'tps';
  if (/permiso de trabajo|work permit|\bead\b|autorizacion de empleo|employment authorization/.test(s)) return 'work_permit';
  if (/\bvisa\b|\bvisado\b/.test(s)) return 'visa_holder';
  if (/en proceso|en tramite|pendiente|pending|in process|aplicando/.test(s)) return 'pending';
  if (/\botro\b|\botra\b|\bother\b/.test(s)) return 'other';
  return '';
}

// ── EL FILTRO DE LA LISTA DE CLIENTES ──────────────────────────────────────
//
// Lissette, 2026-09-15: *"en clientes colocar un filtro por estatus
// migratorio"*.
//
// Vive AQUI y no en la pantalla por lo que dice la cabecera de este archivo: la
// lista ya se habia escrito tres veces y se habian ido separando. Un filtro es
// una cuarta lectura del mismo catalogo, asi que se escribe donde estan las
// otras — y hereda gratis los ALIAS, que es lo que hace que una ficha antigua
// guardada como `asylum` salga al filtrar por "Asilado / Refugiado" en vez de
// quedarse invisible.
//
// Filtra por el estatus del TITULAR. El del conyuge y el de los dependientes
// viven en sus propios campos y son otra pregunta ("¿quien en esta casa
// tiene...?"); mezclarlos aqui haria que una ficha saliera en un filtro por un
// dato que no es el suyo.

/** La marca del filtro para "fichas sin estatus puesto". */
export const SIN_ESTATUS = '__sin_estatus__';

/**
 * ¿Pasa esta ficha el filtro? `'all'` lo desactiva.
 *
 * `SIN_ESTATUS` saca justo las fichas a las que nunca se les rellenó la
 * casilla — que es como se encuentra lo que hay que completar, en vez de
 * adivinarlo (el mismo criterio que el filtro de estado, E-146).
 */
export function pasaElFiltroDeEstatusMigratorio(cliente, filtro) {
  if (!filtro || filtro === 'all') return true;
  const suyo = normalizarEstatus(cliente && cliente.immigrationStatus);
  if (filtro === SIN_ESTATUS) return !suyo;
  return suyo === normalizarEstatus(filtro);
}

/**
 * Las opciones del desplegable, con CUÁNTAS fichas hay detrás de cada una.
 *
 * El número va dentro a propósito: un filtro que dice cuántas hay evita el
 * clic a ciegas que no devuelve nada (mismo criterio que los años de póliza y
 * que las etiquetas). Solo se ofrecen los estatus que alguien tiene de verdad
 * — una lista de diez opciones donde ocho dan cero es ruido.
 *
 * Se ordena de más a menos, y a igualdad por el orden del catálogo, que es el
 * que la agente ya conoce de la ficha.
 */
export function contarEstatusMigratorio(clientes, es = true) {
  const lista = Array.isArray(clientes) ? clientes : [];
  const cuenta = new Map();
  let sinEstatus = 0;
  for (const c of lista) {
    const k = normalizarEstatus(c && c.immigrationStatus);
    if (!k) { sinEstatus += 1; continue; }
    cuenta.set(k, (cuenta.get(k) || 0) + 1);
  }
  const orden = (k) => {
    const i = ESTATUS_MIGRATORIOS.indexOf(k);
    return i === -1 ? ESTATUS_MIGRATORIOS.length : i;
  };
  const opciones = [...cuenta.entries()]
    .map(([id, n]) => ({ id, n, label: etiquetaDeEstatus(id, es) }))
    .sort((a, b) => (b.n - a.n) || (orden(a.id) - orden(b.id)) || a.label.localeCompare(b.label, es ? 'es' : 'en'));
  return { sinEstatus, opciones };
}

/**
 * EDITAR Y BORRAR LOS ESTATUS PROPIOS (Lissette 2026-09-29): *"en la ficha del
 * cliente poder editar los estatus migratorios que se crean personalizados o
 * borrarlos también"*.
 *
 * Renombrar cambia el NOMBRE y deja el ID: las fichas guardan el id, así que
 * todas las que lo usan pasan a leer el nombre nuevo sin tocar ninguna. Por
 * eso no se "migra" nada.
 *
 * Devuelven una lista NUEVA (no tocan el catálogo); quien las llama la guarda
 * con setAgencyEstatusMigratorios + apiUpdateAgencySettings, igual que al crear.
 */
export function renombrarEstatusPropio(lista, id, nombreNuevo) {
  const nombre = String(nombreNuevo ?? '').trim();
  if (!nombre) return { error: 'vacio' };
  const norm = (x) => String(x ?? '').trim().toLowerCase();
  const propios = Array.isArray(lista) ? lista : [];
  if (!propios.some((x) => x.id === id)) return { error: 'no_existe' };
  // El mismo nombre que otro (propio o de fábrica) haría dos opciones iguales
  // en la lista: es el gemelo que el alta ya evita.
  const deFabrica = etiquetasDeEstatus(true);
  const deFabricaEn = etiquetasDeEstatus(false);
  const choca = propios.some((x) => x.id !== id && norm(x.nombre) === norm(nombre))
    || ESTATUS_MIGRATORIOS.some((k) => norm(deFabrica[k]) === norm(nombre) || norm(deFabricaEn[k]) === norm(nombre));
  if (choca) return { error: 'duplicado' };
  return { lista: propios.map((x) => (x.id === id ? { ...x, nombre, nombreEn: nombre } : { ...x })) };
}

/**
 * Quitar uno del catálogo. Las fichas que ya lo tenían NO se tocan: siguen
 * guardando su id y el desplegable lo sigue enseñando en ESA ficha (ver
 * ImmigrationSelect), solo deja de ofrecerse para las nuevas.
 */
export function quitarEstatusPropio(lista, id) {
  return (Array.isArray(lista) ? lista : []).filter((x) => x.id !== id).map((x) => ({ ...x }));
}
